import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { classroomsApi, ClassroomFilters, ClassroomCreate, ClassroomUpdate, ClassroomWithStats } from '../../services/api/classrooms';
import { Classroom } from '../../types/api';

export const useClassrooms = (filters?: ClassroomFilters) => {
  return useQuery({
    queryKey: ['classrooms', filters],
    queryFn: () => classroomsApi.getClassrooms(filters),
  });
};

export const useClassroomsWithStats = (filters?: ClassroomFilters) => {
  return useQuery<ClassroomWithStats[]>({
    queryKey: ['classrooms', 'with-stats', filters],
    queryFn: () => classroomsApi.getClassroomsWithStats(filters),
  });
};

export const useClassroom = (id: string, enabled = true) => {
  return useQuery<Classroom>({
    queryKey: ['classroom', id],
    queryFn: () => classroomsApi.getClassroom(id),
    enabled: enabled && !!id,
  });
};

export const useClassroomStats = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ['classroom', id, 'stats'],
    queryFn: () => classroomsApi.getClassroomStats(id),
    enabled: enabled && !!id,
  });
};

export const useCreateClassroom = () => {
  const queryClient = useQueryClient();

  return useMutation<Classroom, Error, ClassroomCreate>({
    mutationFn: (data) => classroomsApi.createClassroom(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classrooms'] });
    },
  });
};

export const useUpdateClassroom = () => {
  const queryClient = useQueryClient();

  return useMutation<Classroom, Error, { id: string; data: ClassroomUpdate }>({
    mutationFn: ({ id, data }) => classroomsApi.updateClassroom(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classrooms'] });
    },
  });
};

export const useDeleteClassroom = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id) => classroomsApi.deleteClassroom(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classrooms'] });
    },
  });
};
